import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { PrintService, BrowserLabelItems } from '../../../service/print.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { ToastService } from '../../../service/toast.service';
import { ReceivingLogApiService } from '../../../domain/receiving-log/api/receiving-log-api.service';
import { PrintKind, PrintFromLogRequest } from '../../../models/printing/PrintRequest';
import { PrinterInfo } from '../../../models/printing/PrinterInfo';
import { ReceivingLogRow } from '../../../models/logs/ReceivingLogRow';
import { NewLog } from '../../../models/logs/NewLog';
import { Location } from '../../../models/assets-data/LocationConfig.interface';
import { ReprintItem } from '../../../models/printing/ReprintItem.interface';
import { buildPrintQr, splitQrFromLogRequests } from '../../../domain/receiving-log/rules/qr-items.rules';
import { itemCaption } from '../../../domain/receiving-log/rules/container-items.rules';
import { ReceivingItemRef } from '../../../models/logs/ReceivingItemRef';
import {
  locationHasBackendPrinter,
  browserQrEnabled,
  labelAutomatic,
  sheetAutomatic,
  resolvePrintRoute,
  PrintDoc,
  PrintRoute,
} from '../../../domain/receiving-log/rules/print-path.rules';
import { PrintFallbackService } from '../../../service/print-fallback.service';
import { PrintRouteContextService } from '../../../service/print-route-context.service';
import { ContactsService } from '../../../service/contact.service';
import { todaysDateString } from '../../../utils/date-functions';
import { environment } from '../../../../environments/environment';
import { QR_BASE_URL } from '../../../shared/constants/qr-base-url';

/**
 * reprint-labels-modal.component.ts
 *
 * Configure-and-reprint modal for a stored receiving log, opened from the log
 * table's row actions (available to all users, like the email action). Mirrors
 * the mobile Review step's print sheet: choose what to print (label / copier
 * sheet / both), the copy counts, and the printer(s) — then submit to
 * `POST /V1/print/from-log/{logId}` via {@link PrintService.submitPrintJobFromLog}.
 *
 * The request is intent-only for the text fields: the server fills PO / job /
 * vendor / items / etc. from the stored log. The frontend still owns the per-item
 * QR — exactly as after a fresh submit — building the `qr` block from the log's
 * item rows and this environment's app base URL. The from-log endpoint passes
 * that `qr` block straight through to the Print API; it does not build URLs itself.
 *
 * The item rows are fetched lazily when the modal opens (one small request per
 * reprint via {@link ReceivingLogApiService.getReceivingLogItems}), rather than
 * enriching every row of the log table up front. Each row carries its own stored
 * `itemType`/`typeIndex`/`typeTotal`, so a reprint of a mixed delivery captions
 * every label the way it was captioned the first time.
 *
 * When the log has item rows, reprint is PER ITEM: the shared
 * {@link ItemReprintListComponent} shows one row per physical item ("BOX 1 of 3")
 * with independent Labels / Copier-sheets counts, each printed on demand via
 * {@link PrintService.submitPrintJobFromLogBatch} (a "Both" row whose counts
 * differ fans out into two from-log calls). A log with no item rows (legacy log,
 * or a failed lookup) falls back to the whole-log "Send to printer" action with
 * no `qr` block, printing the no-QR document.
 */
@Component({
  selector: 'app-reprint-labels-modal',
  templateUrl: './reprint-labels-modal.component.html',
  styleUrls: ['./reprint-labels-modal.component.css'],
})
export class ReprintLabelsModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() logRow: ReceivingLogRow | null = null;

  @Output() closeModal = new EventEmitter<void>();

  public printKind: PrintKind = 'Both';
  public labelCount: number = 1;
  public copyCount: number = 1;
  public selectedZebraPrinter: string = '';
  public selectedCopierPrinter: string = '';

  public availablePrinters: PrinterInfo[] = [];
  public printersLoading: boolean = false;
  public isSending: boolean = false;

  /**
   * Lazily-fetched per-item rows for the open log, ordered by ItemIndex — each
   * item's GUID plus the stored type facts its label is captioned with. Empty for
   * a legacy log that never captured a structured quantity.
   */
  private _items: ReceivingItemRef[] = [];
  /** True while the item fetch is in flight — Confirm is blocked so we never
   *  submit an empty `qr` for a log that actually has items. */
  public itemIdsLoading: boolean = false;

  /**
   * True when the lookup succeeded but the log has no item rows — a legacy
   * submission. The reprint still works (one plain label, no QR), but the
   * missing QR is surfaced so nobody reads it as a bug.
   */
  public hasNoItemRows: boolean = false;

  /**
   * One row per physical item (e.g. "BOX 1 of 3") with its own label/sheet copy
   * counts, rendered by the shared ItemReprintListComponent. Populated after the
   * item GUIDs load; empty for a legacy log (which falls back to the whole-log
   * "Send to printer" path below).
   */
  public reprintItems: ReprintItem[] = [];
  /** Batch state of the single per-item reprint button (all items print together). */
  public reprintStatus: 'idle' | 'sending' | 'printed' = 'idle';

  /** True when the log has per-item QR codes — reprint is per item, not whole-log. */
  public get hasItemReprint(): boolean { return this.reprintItems.length > 0; }

  /**
   * Whether the row's facility has a Zebra printer the print API can target
   * (today, only Kent). Everywhere else this modal reprints the LABEL through the
   * browser instead — see {@link usesBrowserPrint}.
   */
  public get printingSupportedAtLocation(): boolean {
    return locationHasBackendPrinter(this._facilityLocation());
  }

  /**
   * Whether this reprint would carry QR codes if the log had item rows.
   *
   * Always true on the print API path — everything it prints is scannable. On the
   * browser path it is the facility's setting. Used to decide whether "this log
   * has no tracked items, so no QR codes" is news: at a facility that prints no
   * browser QR codes anyway, that sentence explains an absence nobody would have
   * expected filled.
   */
  public get qrExpected(): boolean {
    return !this.usesBrowserPrint || this.browserQr;
  }

  /**
   * Whether browser-rendered documents from this modal carry per-item QR codes,
   * per document.
   *
   * A facility property, not a per-reprint choice: a reprint must produce the
   * same sticker the original print did, or the box ends up carrying two labels
   * that disagree about whether it is scannable. Per document because the answer
   * differs — the code scans on the copier sheet and not on the Dymo label stock.
   */
  public get browserQrLabel(): boolean {
    return browserQrEnabled(this._facilityLocation(), 'label');
  }

  public get browserQrSheet(): boolean {
    return browserQrEnabled(this._facilityLocation(), 'sheet');
  }

  /** Either document — for the copy that is about the reprint as a whole. */
  public get browserQr(): boolean {
    return this.browserQrLabel || this.browserQrSheet;
  }

  /**
   * True when this reprint renders in the browser rather than going to the print
   * API.
   *
   * A facility with no Zebra printer configured would have its from-log job
   * queued at hardware that does not exist, so those sites keep the original
   * browser-print behaviour — the label is rendered client-side and sent through
   * the browser's print dialog to whatever printer the machine has (typically a
   * Dymo). This is also the path the mobile flow points people at: outside Kent
   * the flow disables printing at submit and tells them to print from this table.
   *
   * Transitional, like the flow's equivalent: fill in a facility's
   * `zebraPrinterName` and it moves onto the API path with no code change.
   *
   * It also honours this device's print-fallback mode, and that is not a nicety:
   * Kent is the only facility with a Zebra, so Kent is the only place the Zebra
   * can be down — and this modal is exactly where the mobile flow sends people
   * when it defers a label ("print it from the log table"). Left keyed on
   * location alone, it would queue that reprint at the same dead printer and
   * make the instruction a lie.
   */
  public get usesBrowserPrint(): boolean {
    return this.printRoute.backendKind === null;
  }

  /**
   * Where this reprint's documents go, per document. Under "Zebra is down" a
   * Both reprint still sends its copier sheet to the copier through the API and
   * renders only the label here.
   *
   * The context comes from the shared provider, built for THIS ROW'S facility
   * rather than the one selected in the header — a reprint routes to the building
   * the delivery went to. The surface is read honestly too: this modal used to
   * declare itself desktop-only because it opens from the log table, but a phone
   * reaches that table as readily as a desk does, and there the browser print
   * dialog reaches no printer. See {@link deferredMessage}.
   */
  public get printRoute(): PrintRoute {
    return resolvePrintRoute(
      this.printKind,
      this._printRouteContext.forLocation(this._facilityLocation()),
    );
  }

  /**
   * Documents this reprint asked for that cannot print from this device at all —
   * a phone under a printer outage, where the backend is ruled out and the
   * browser dialog reaches nothing.
   *
   * Empty in normal mode even on a phone: {@link resolvePrintRoute} deliberately
   * lets a phone browser-print there, and revisiting that is not this modal's
   * call.
   */
  public get deferredDocs(): PrintDoc[] {
    return this.printRoute.deferredDocs;
  }

  /** Whether any part of this reprint has to happen somewhere else. */
  public get hasDeferredPrinting(): boolean {
    return this.deferredDocs.length > 0;
  }

  /** Whether NOTHING can print here — the Confirm action has no job to send. */
  public get allDeferred(): boolean {
    const route = this.printRoute;
    return route.backendKind === null && route.browserDocs.length === 0;
  }

  /**
   * What the operator has to do about the documents that cannot print here.
   *
   * Names the documents and the row, then the route — the same three parts, in
   * the same order, as the mobile flow's Done screen. The instruction differs in
   * one way that matters: they are already looking at the reprint they wanted, so
   * telling them to "reprint from the log table" would send them in a circle. The
   * thing they lack is a desktop, so that is what the sentence asks for.
   */
  public get deferredMessage(): string {
    const docs = this.deferredDocs.includes('label') && this.deferredDocs.includes('sheet')
      ? 'Labels and copier sheets'
      : this.deferredDocs.includes('label') ? 'Labels' : 'Copier sheets';
    const po = this.logRow?.poNumber ? `PO ${this.logRow.poNumber}` : 'this delivery';
    return `${docs} for ${po} can't print from a phone. `
      + 'Open the Receiving Log on a desktop computer and reprint this row there.';
  }

  /**
   * Which documents of THIS reprint render in the browser, and why.
   *
   * These replaced one `labelsDivertedByOutage` that asked a document-blind
   * question — "is any override on, and is anything rendering here?" — and then
   * answered it with label-specific prose. An operator who marked the COPIER down
   * and asked for a copier sheet was told the Zebra was down and that their label
   * would open in a print dialog: two wrong nouns in a sentence about a document
   * that was not in the job.
   */
  public get labelInBrowser(): boolean {
    return this.printRoute.browserDocs.includes('label');
  }

  public get sheetInBrowser(): boolean {
    return this.printRoute.browserDocs.includes('sheet');
  }

  /**
   * Whether that document is in the browser because this DEVICE reports its
   * printer down, rather than because the facility has no such printer. The two
   * are different news: the first is temporary and the operator set it, the
   * second is how this location prints every day.
   */
  public get labelDivertedByOutage(): boolean {
    return this.labelInBrowser && !labelAutomatic(this._printFallback.mode);
  }

  public get sheetDivertedByOutage(): boolean {
    return this.sheetInBrowser && !sheetAutomatic(this._printFallback.mode);
  }

  /** Whether anything rendering in the browser here carries a QR code to shrink. */
  public get browserDocsCarryQr(): boolean {
    return (this.labelInBrowser && this.browserQrLabel)
      || (this.sheetInBrowser && this.browserQrSheet);
  }

  /**
   * Which named-printer selects are worth showing: only the documents actually
   * going to the print API. A Zebra select on a job whose label is being rendered
   * in the browser would be a control with nothing behind it.
   */
  public get sendsZebraToBackend(): boolean {
    return this.printRoute.backendKind === 'Zebra' || this.printRoute.backendKind === 'Both';
  }

  public get sendsCopierToBackend(): boolean {
    return this.printRoute.backendKind === 'Copier' || this.printRoute.backendKind === 'Both';
  }

  /**
   * Per-item reprint rows are offered whenever ANY document of this reprint still
   * reaches the print API; a reprint with nothing left on the backend falls back
   * to the whole-log quantity spinners.
   *
   * Note what this switches BESIDES the counts: in per-item mode the footer button
   * is "Done" and {@link onConfirm} is not rendered at all, so `onReprintAll` is the
   * only print action on screen. That is why it, and not just Confirm, has to route
   * the browser half — see the comment there.
   *
   * The rows themselves are no longer backend-only. They were, because they were
   * wired to the from-log endpoint's per-item copies block; a browser-rendered
   * document is per-container too and reads the same counts (see
   * {@link _browserItems}), so a split job shows a count for each document
   * regardless of which path it takes. Which control a browser-ONLY facility should
   * see is still open: it gets the whole-log spinners.
   */
  public get showItemReprint(): boolean {
    return this.hasItemReprint && !this.usesBrowserPrint;
  }

  private _printersSub: Subscription | null = null;
  private _printersLoadingSub: Subscription | null = null;
  private _sendingSub: Subscription | null = null;
  private _itemIdsSub: Subscription | null = null;

  constructor(
    private _printService: PrintService,
    private _assetsDataService: AssetsDataService,
    private _receivingLogApi: ReceivingLogApiService,
    private _toastService: ToastService,
    private _contactsService: ContactsService,
    private _printFallback: PrintFallbackService,
    private _printRouteContext: PrintRouteContextService,
  ) {}

  ngOnInit(): void {
    this._printersSub = this._printService.availablePrinters.subscribe(p => this.availablePrinters = p);
    this._printersLoadingSub = this._printService.printersLoading.subscribe(l => this.printersLoading = l);
    this._sendingSub = this._printService.printingLoading.subscribe(l => this.isSending = l);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // On open, reset the config to defaults for the row's facility and ensure the
    // live printer list is loaded for the optional override.
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this._resetForRow();

      // The printer list serves the print-API path only — it populates the
      // named-printer override, and the browser path has no printer to name.
      if (!this.usesBrowserPrint && this.availablePrinters.length === 0) {
        this._printService.fetchPrinters();
      }

      // The item GUIDs are needed on BOTH paths: browser-rendered labels now carry
      // a per-item QR too, so skipping this fetch on the browser path would emit
      // containers with nothing scannable on them — and this modal is exactly
      // where the flow sends people when the Zebra is down, which is when a
      // browser-rendered label is the only label there is.
      this._loadItemIds();
    }
  }

  ngOnDestroy(): void {
    this._printersSub?.unsubscribe();
    this._printersLoadingSub?.unsubscribe();
    this._sendingSub?.unsubscribe();
    this._itemIdsSub?.unsubscribe();
  }

  public get showZebra(): boolean { return this.printKind === 'Zebra' || this.printKind === 'Both'; }
  public get showCopier(): boolean { return this.printKind === 'Copier' || this.printKind === 'Both'; }

  /** Names in the live printer list (to know whether a configured default needs a synthetic option). */
  public get printerNames(): string[] {
    return this.availablePrinters.map(p => p.name);
  }

  public incLabel(): void { this.labelCount += 1; }
  public decLabel(): void { this.labelCount = Math.max(1, this.labelCount - 1); }
  public incCopy(): void { this.copyCount += 1; }
  public decCopy(): void { this.copyCount = Math.max(1, this.copyCount - 1); }

  /**
   * The from-log endpoint returns 400 if the log lacks the number the chosen
   * document needs: jobNumber for the Zebra (shop label), poNumber for the
   * copier sheet. Guard here so we don't fire a request that can only fail.
   *
   * These are constraints of that endpoint, not of printing — the browser
   * templates simply render the field empty. Blocking there would leave an older
   * log with no job number unprintable at exactly the facilities that are told to
   * print from this table, so the guards don't apply on the browser path.
   */
  public get missingJobNumber(): boolean {
    const backendKind = this.printRoute.backendKind;
    const sendsZebra = backendKind === 'Zebra' || backendKind === 'Both';
    return sendsZebra && !this.logRow?.jobNumber?.trim();
  }

  public get missingPoNumber(): boolean {
    const backendKind = this.printRoute.backendKind;
    const sendsCopier = backendKind === 'Copier' || backendKind === 'Both';
    return sendsCopier && !this.logRow?.poNumber?.trim();
  }

  /**
   * The "contact" name printed on the labels. The stored log has no contact
   * field, only an address, so the name is resolved from the cached employee
   * directory — a reprint prints the same "Bob Smith" the original did. Falls
   * back to picking a name out of the address (e.g. "john.d.doe@…" → "John D
   * Doe") for anyone the directory doesn't list. Undefined when there's no email.
   */
  private get contactName(): string | undefined {
    const email = this.logRow?.email?.trim();
    return email ? this._contactsService.resolveName(email) : undefined;
  }

  public get canSubmit(): boolean {
    return !this.isSending && !this.itemIdsLoading && !!this.logRow?.id
      && !this.allDeferred
      && !this.missingJobNumber && !this.missingPoNumber;
  }

  public onClose(): void {
    this.closeModal.emit();
  }

  /** Builds the intent-only request and submits it to the from-log endpoint. */
  public onConfirm(): void {
    if (!this.canSubmit || !this.logRow?.id) { return; }

    const route = this.printRoute;

    // TEMPORARY DIAGNOSTIC — remove once the missing copier dialog is understood.
    // Prints the routing decision this reprint is about to act on, so a run that
    // opens no dialog says whether the sheet was even handed to the browser.
    console.log('[reprint diagnostic]', {
      chosenKind: this.printKind,
      storedFallbackMode: this._printFallback.mode,
      backendKind: route.backendKind,
      browserDocs: route.browserDocs,
      deferredDocs: route.deferredDocs,
      itemRows: this._items.length,
    });

    // Whatever the API can't take is rendered here: no printer at this facility,
    // or a printer this device has been told is down. With the Zebra down and a
    // Both reprint, this prints the label and the request below still sends the
    // copier sheet to the copier.
    if (route.browserDocs.length > 0) {
      const printed = this._browserPrint(
        route.browserDocs.includes('label'),
        route.browserDocs.includes('sheet'),
      );
      if (!route.backendKind) {
        if (printed) { this.onClose(); }
        return;
      }
    }

    // Build the per-item QR the same way the submit path does: one finished URL
    // per item ({QR_BASE_URL}/view-logged-item?payload={guid}),
    // with each item's stored type and "X of N" numbering mapped straight through
    // from the rows fetched on open. A legacy log with no item rows yields
    // undefined, which omits `qr` and prints the no-QR document.
    const qr = buildPrintQr(QR_BASE_URL, this._items);

    // The kind the ROUTE left on the backend, not the kind the user chose: with
    // the Zebra down that is 'Copier' even though the modal says "Both", and the
    // label has just been rendered in the browser above.
    const backendKind = route.backendKind;
    // Nothing left for the backend: either the browser half above was the whole
    // job, or this is a phone under an outage and every document is deferred —
    // which `canSubmit` already blocks, with `deferredMessage` saying why.
    if (!backendKind) { return; }
    const sendsZebra = backendKind === 'Zebra' || backendKind === 'Both';
    const sendsCopier = backendKind === 'Copier' || backendKind === 'Both';

    const request: PrintFromLogRequest = {
      kind: backendKind,
      zebraPrinterName: sendsZebra ? (this.selectedZebraPrinter || undefined) : undefined,
      copierPrinterName: sendsCopier ? (this.selectedCopierPrinter || undefined) : undefined,
      labelCount: this.labelCount,
      copyCount: this.copyCount,
      contact: this.contactName,
      qr,
    };

    this._printService.submitPrintJobFromLog(
      this.logRow.id,
      request,
      () => this.onClose(),
      (docs) => this._browserPrint(docs.includes('label'), docs.includes('sheet')),
    );
  }

  /**
   * Lazily fetches the open log's per-item rows so `onConfirm` can build the QR.
   * Confirm is blocked while this is in flight (see {@link canSubmit}). A failure
   * degrades to an empty list — the reprint then prints the no-QR label rather
   * than blocking — but we warn so the degradation isn't silent.
   *
   * A log with ONE row holding a free-text string ("3 boxes and 2 pallets" typed
   * into a single row) reprints as-is: one row, one QR, one label. Splitting or
   * parsing it here would either orphan the GUID already on a sticker in the
   * warehouse or mint new ones that kill every label already printed — and the
   * parse is a guess that can put "BOX 2 of 3" on a pallet. Reprint never mutates
   * rows; printing again produces the labels it produced before.
   */
  private _loadItemIds(): void {
    this._items = [];
    this.reprintItems = [];
    this.hasNoItemRows = false;
    const logId = this.logRow?.id;
    if (!logId) { this.itemIdsLoading = false; return; }

    this._itemIdsSub?.unsubscribe();
    this.itemIdsLoading = true;
    this._itemIdsSub = this._receivingLogApi.getReceivingLogItems(logId).subscribe({
      next: (items) => {
        this._items = items ?? [];
        this._buildReprintItems();
        // No item rows: a legacy submission that never captured a structured
        // quantity. The reprint prints one plain label, with labelCount governing
        // the copies — say so rather than letting a missing QR look like a fault.
        this.hasNoItemRows = this._items.length === 0;
        this.itemIdsLoading = false;
      },
      error: () => {
        this._items = [];
        this.reprintItems = [];
        this.itemIdsLoading = false;
        this._toastService.showWarningMessage(
          'Could not load item codes for this log; it will print without QR codes.'
        );
      },
    });
  }

  /**
   * Builds one reprint row per item ("BOX 1 of 3", "PALLET 1 of 2"…), each
   * starting at 1 copy of each document. The caption comes from the row's OWN
   * stored type and numbering, so a mixed delivery numbers each type separately;
   * a lone item of its type drops the "1 of 1" suffix.
   */
  private _buildReprintItems(): void {
    this.reprintItems = this._items.map((item, i) => ({
      index: i,
      title: itemCaption(item),
      labelCopies: 1,
      sheetCopies: 1,
      status: 'idle' as const,
    }));
  }

  /**
   * Reprints the per-item labels/sheets in ONE job: the QR block carries every
   * item, each with its own stored "X of N" numbering and its own copy count in
   * `qr.items[].copies`. Copies are clamped to at least 1, so every item on the
   * list prints. A "Both" job whose label and sheet counts differ fans out into
   * two from-log calls (see splitQrFromLogRequests).
   *
   * Routes per document, like {@link onConfirm}: the browser half renders here and
   * only what is left goes to the endpoint. This is the ONLY print action on screen
   * in per-item mode, so anything it does not handle is not printed at all.
   */
  public onReprintAll(): void {
    const logId = this.logRow?.id;
    if (!logId) { return; }
    const route = this.printRoute;
    if (this.missingJobNumber || this.missingPoNumber) {
      this._toastService.showWarningMessage(
        this.missingJobNumber
          ? 'This log has no job number, so the label can’t be reprinted.'
          : 'This log has no PO number, so the copier sheet can’t be reprinted.'
      );
      return;
    }

    // Whatever the route left OFF the backend renders here, exactly as `onConfirm`
    // does it. This used to look at `backendKind` alone and return when it was
    // null, which meant the browser half of a per-item reprint had no code path at
    // all: in per-item mode the footer button is "Done" (see {@link showItemReprint}),
    // so `onConfirm` never runs, and this was the only print action on screen. A
    // "Both" reprint at a facility with a Zebra but no automatic copier therefore
    // sent the label to the API and dropped the copier sheet silently — no dialog,
    // no error, and no log line, because every statement that would have said so
    // lives in the method that was never called.
    if (route.browserDocs.length > 0) {
      // Mirrors the backend half's empty-batch check below: an all-zero set renders
      // an empty document, which opens a dialog on a blank page rather than saying
      // nothing was asked for.
      if (!this._browserCopiesRequested(route.browserDocs)) {
        this.reprintStatus = 'idle';
        this._toastService.showWarningMessage('Set at least one item to 1 or more copies to print.');
        return;
      }
      const printed = this._browserPrint(
        route.browserDocs.includes('label'),
        route.browserDocs.includes('sheet'),
      );
      // Nothing left for the API: the browser half was the whole job.
      if (!route.backendKind) {
        this.reprintStatus = printed ? 'printed' : 'idle';
        return;
      }
    }

    // Only what the route left on the backend goes in the request — with the Zebra
    // down and "Both" chosen, that is the copier sheets, the labels having just
    // been rendered above.
    const backendKind = route.backendKind;
    if (!backendKind) { return; }

    this.reprintStatus = 'sending';
    const labelCopies = this.reprintItems.map(r => r.labelCopies);
    const sheetCopies = this.reprintItems.map(r => r.sheetCopies);
    const sendsZebra = backendKind === 'Zebra' || backendKind === 'Both';
    const sendsCopier = backendKind === 'Copier' || backendKind === 'Both';
    const base: PrintFromLogRequest = {
      kind: backendKind,
      zebraPrinterName: sendsZebra ? (this.selectedZebraPrinter || undefined) : undefined,
      copierPrinterName: sendsCopier ? (this.selectedCopierPrinter || undefined) : undefined,
      contact: this.contactName,
      qr: buildPrintQr(QR_BASE_URL, this._items),
    };
    const requests = splitQrFromLogRequests(base, labelCopies, sheetCopies);
    // Every item was set to zero copies: there is nothing to print, so re-arm the
    // button rather than leaving it spinning on an empty job.
    if (requests.length === 0) {
      this.reprintStatus = 'idle';
      this._toastService.showWarningMessage('Set at least one item to 1 or more copies to print.');
      return;
    }
    this._printService.submitPrintJobFromLogBatch(
      logId,
      requests,
      () => { this.reprintStatus = 'printed'; },
      (docs) => this._browserPrint(docs.includes('label'), docs.includes('sheet')),
    );
  }

  /** A per-item count changed — re-arm the printed batch so it invites another print. */
  public onReprintChanged(): void {
    if (this.reprintStatus === 'printed') { this.reprintStatus = 'idle'; }
  }

  /**
   * Browser reprint: renders the label(s) client-side and sends them through the
   * browser's print dialog, for facilities with no print-API printer.
   *
   * Unlike the from-log endpoint — which fills the text fields from the stored log
   * server-side — the browser templates are populated here, so the row's own
   * fields are mapped into a NewLog. The per-item QR codes and "BOX n of N"
   * captions are rendered here too, from the item rows fetched on open, so a
   * reprint done during a printer outage identifies its containers the same way
   * the print API's labels do.
   *
   * @param printLabel - Render the Dymo label.
   * @param printSheet - Render the full-sheet document.
   * @returns Whether the print dialog opened; the caller decides whether that
   *   finishes the job (and closes the modal) or whether a backend half follows.
   */
  private _browserPrint(printLabel: boolean, printSheet: boolean): boolean {
    const labelData = this._toNewLog();
    try {
      if (printLabel) { this._printService.printDymoLabel(labelData, this._browserItems('label')); }
      if (printSheet) { this._printService.printLargeLabel(labelData, this._browserItems('sheet')); }
      return true;
    } catch (e) {
      console.error('Browser reprint failed:', e);
      this._toastService.showWarningMessage('Could not open the print dialog. Please try again.');
      return false;
    }
  }

  /**
   * The per-container context for a browser-rendered document: one label per item
   * row, each with its own QR code and its own stored "BOX n of N" caption.
   *
   * Which copy counts apply depends on which control the operator actually had.
   * When the per-item rows are on screen — a split job, where the label renders
   * here while the copier sheet still goes to the copier — those counts govern.
   * When they are hidden, the whole-log quantity spinner was the only control, so
   * it applies to every container.
   *
   * Undefined for a legacy log with no item rows, which keeps the service on its
   * repeat-one-label path.
   */
  private _browserItems(doc: PrintDoc): BrowserLabelItems | undefined {
    if (this._items.length === 0) { return undefined; }
    const uniform = doc === 'label' ? this.labelCount : this.copyCount;
    const copies = this.showItemReprint
      ? this.reprintItems.map((r) => (doc === 'label' ? r.labelCopies : r.sheetCopies))
      : this._items.map(() => uniform);
    const includeQr = doc === 'label' ? this.browserQrLabel : this.browserQrSheet;
    return { items: this._items, appBaseUrl: QR_BASE_URL, copies, includeQr };
  }

  /**
   * Whether the counts on screen ask for at least one copy of anything routed to
   * the browser — the browser-side equivalent of the empty-batch check
   * {@link splitQrFromLogRequests} performs for the print API.
   *
   * A legacy log has no per-item context ({@link _browserItems} returns undefined)
   * and prints from the whole-log spinner, whose floor is 1, so there is nothing
   * to reject there.
   */
  private _browserCopiesRequested(docs: PrintDoc[]): boolean {
    return docs.some((doc) => {
      const copies = this._browserItems(doc)?.copies;
      return !copies || copies.some((count) => count > 0);
    });
  }

  /**
   * Maps the stored log row onto the NewLog shape the browser label templates
   * read. `shelfLocation` comes from the row's `location` (where the delivery was
   * put), not its storage facility.
   */
  private _toNewLog(): NewLog {
    const row = this.logRow;
    return new NewLog({
      poNumber: row?.poNumber || '',
      vendor: row?.vendor,
      jobNumber: row?.jobNumber,
      jobName: row?.jobName,
      jcDept: row?.jcDept,
      type: row?.shipmentType,
      qtyUnits: row?.quantity,
      shelfLocation: row?.location || '',
      items: row?.items,
      notes: row?.notes,
      contact: this.contactName,
      // Reprints keep the original log's received date, not today's — the label
      // describes when the delivery arrived.
      dateIn: row?.dateIn || todaysDateString(),
      printQtyDymo: this.labelCount,
      printQtySheet: this.copyCount,
    });
  }

  /** Resets the print config to the row's facility defaults each time the modal opens. */
  private _resetForRow(): void {
    this.printKind = 'Both';
    this.labelCount = 1;
    this.copyCount = 1;
    this.reprintItems = [];
    this.reprintStatus = 'idle';
    this.hasNoItemRows = false;
    const location = this._facilityLocation();
    this.selectedZebraPrinter = location?.zebraPrinterName || '';
    this.selectedCopierPrinter = location?.copierPrinterName || '';
  }

  /** The location config for the row's storage facility (for its default printers). */
  private _facilityLocation(): Location | undefined {
    const facility = this.logRow?.storageFacility || this.logRow?.shipmentType;
    if (!facility) { return undefined; }
    return this._assetsDataService.locations?.find(l => l.locationLabel === facility);
  }
}
